import React from 'react';
import { Card as MuiCard, CardContent, CardHeader, CardActions } from '@mui/material';
import PropTypes from 'prop-types';
import { Box, Typography } from '../atoms';

/**
 * Card Molecule
 * A compound component that combines multiple atoms to create a card layout
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Card content
 * @param {string} props.title - Card title
 * @param {string|React.ReactNode} props.subtitle - Card subtitle
 * @param {React.ReactNode} props.icon - Icon to display in the header
 * @param {React.ReactNode} props.headerAction - Action component for the header
 * @param {React.ReactNode} props.actions - Actions for the footer
 * @param {boolean} props.elevated - Whether card should have elevation effect on hover
 * @param {string} props.status - Status indicator (success, warning, error, info)
 * @param {Function} props.onClick - Click handler
 * @param {Object} props.sx - Additional styles
 * @returns {React.ReactElement} Card component
 */
const Card = ({
  children,
  title,
  subtitle,
  icon,
  headerAction,
  actions,
  elevated = false,
  status,
  onClick,
  sx = {},
  ...rest
}) => {
  // Status border colors
  const statusColors = {
    success: 'success.main',
    warning: 'warning.main',
    error: 'error.main',
    info: 'info.main',
  };
  
  // Combine styles
  const cardStyles = {
    position: 'relative',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.3s ease',
    ...(elevated && {
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: (theme) => theme.shadows[4],
      },
    }),
    ...(status && {
      borderLeft: (theme) => `4px solid ${theme.palette[statusColors[status] || 'primary.main']}`,
    }),
    ...(onClick && { cursor: 'pointer' }),
    ...sx,
  };
  
  return (
    <MuiCard sx={cardStyles} onClick={onClick} {...rest}>
      {/* Card Header */}
      {(title || icon) && (
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {icon}
              {title && (
                <Typography variant="h6" component="div">
                  {title}
                </Typography>
              )}
            </Box>
          }
          subheader={subtitle}
          action={headerAction}
        />
      )}
      
      {/* Card Content */}
      <CardContent sx={{ flexGrow: 1, pt: title ? 0 : 2 }}>
        {children}
      </CardContent>
      
      {/* Card Actions */}
      {actions && (
        <CardActions sx={{ justifyContent: 'flex-end', padding: 2 }}>
          {actions}
        </CardActions>
      )}
    </MuiCard>
  );
};

Card.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  icon: PropTypes.node,
  headerAction: PropTypes.node,
  actions: PropTypes.node,
  elevated: PropTypes.bool,
  status: PropTypes.oneOf(['success', 'warning', 'error', 'info']),
  onClick: PropTypes.func,
  sx: PropTypes.object
};

export default Card;